import axios from 'axios';
import router from '../router/router.js';
import { jwtDecode } from 'jwt-decode';

const secureAxios = axios.create({
  // Remove baseURL to avoid adding /api automatically
  timeout: 10000, // Optional: Keep timeout for reliability
});

const TRANSIENT_RETRY_DELAYS = [350, 800, 1600];
const SESSION_REFRESH_WINDOW_SECONDS = 2 * 60 * 60;
const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));
let sessionRefreshPromise = null;
let authRedirectInProgress = false;

const normalizeStoredToken = (token) => {
  if (typeof token !== 'string') return '';
  if (token.length > 1 && token.startsWith('"') && token.endsWith('"')) {
    return token.slice(1, -1);
  }
  return token;
};

const expireSessionOnce = () => {
  if (authRedirectInProgress) return;
  authRedirectInProgress = true;
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  router.push({ path: '/login', query: { sessionExpired: '1' } });
};

const refreshSession = async (token) => {
  if (!sessionRefreshPromise) {
    sessionRefreshPromise = axios.post('/api/users/refresh', {}, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 10000,
    }).then((response) => {
      const refreshedToken = response.data?.token;
      if (!refreshedToken) throw new Error('Session refresh returned no token');
      localStorage.setItem('authToken', refreshedToken);
      authRedirectInProgress = false;
      return refreshedToken;
    }).finally(() => {
      sessionRefreshPromise = null;
    });
  }
  return sessionRefreshPromise;
};

const shouldRetryTransientRead = (error) => {
  const config = error?.config;
  const method = String(config?.method || 'get').toLowerCase();
  if (!config || !['get', 'head'].includes(method)) return false;
  if (!String(config.url || '').startsWith('/api/')) return false;

  const status = error?.response?.status;
  const networkFailure = !error?.response && error?.code !== 'ERR_CANCELED';
  const temporaryGatewayFailure = [502, 503, 504].includes(status);
  // webpack-dev-server returns 500 when its API proxy briefly loses port 3000.
  const localProxyRestart = process.env.NODE_ENV !== 'production' && status === 500;

  return networkFailure || temporaryGatewayFailure || localProxyRestart;
};

// Automatically attach token to every request
secureAxios.interceptors.request.use(
  async (config) => {
    let token = normalizeStoredToken(localStorage.getItem('authToken'));
    if (token) {
      const isAuthenticationRequest = ['/api/users/login', '/api/users/refresh']
        .some((path) => String(config.url || '').startsWith(path));

      if (!isAuthenticationRequest) {
        try {
          const decoded = jwtDecode(token);
          const secondsRemaining = Number(decoded?.exp || 0) - Math.floor(Date.now() / 1000);
          if (secondsRemaining > 0 && secondsRemaining <= SESSION_REFRESH_WINDOW_SECONDS) {
            try {
              token = await refreshSession(token);
            } catch (refreshError) {
              // A temporary network failure should not discard a token that is
              // still valid. Let the original request try with that token.
              if ([401, 403].includes(refreshError?.response?.status)) {
                expireSessionOnce();
                return Promise.reject(refreshError);
              }
            }
          } else if (secondsRemaining <= 0) {
            expireSessionOnce();
            return Promise.reject(new Error('Session expired'));
          } else {
            authRedirectInProgress = false;
          }
        } catch (decodeError) {
          expireSessionOnce();
          return Promise.reject(decodeError);
        }
      }
      config.headers.Authorization = `Bearer ${token}`;

      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const studioId = Number(user.studio_id);
        if ((['carolina.r@mgrouplcs.com', 'jono@mgrouplcs.com', 'jorge.n@mgrouplcs.com'].includes(String(user.email || '').trim().toLowerCase()) || ['accountant', 'support'].includes(user.role))
          && (user.can_switch_studio === true || Number(user.can_switch_studio) === 1)
          && Number.isInteger(studioId) && studioId > 0) {
          config.headers['X-Studio-Id'] = String(studioId);
        }
      } catch {
        // A malformed cached user must not prevent an authenticated request.
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for handling auth errors
secureAxios.interceptors.response.use(
  (response) => {
    const method = String(response?.config?.method || 'get').toLowerCase();
    const url = String(response?.config?.url || '');
    if (!['get', 'head'].includes(method)
      && (url.startsWith('/api/card-expenses') || url.startsWith('/api/adjustments'))) {
      window.dispatchEvent(new CustomEvent('card-expenses-updated'));
    }
    return response;
  },
  async (error) => {
    if (shouldRetryTransientRead(error)) {
      const config = error.config;
      const retryCount = Number(config.__transientRetryCount || 0);
      if (retryCount < TRANSIENT_RETRY_DELAYS.length) {
        config.__transientRetryCount = retryCount + 1;
        await wait(TRANSIENT_RETRY_DELAYS[retryCount]);
        return secureAxios.request(config);
      }
    }

    const status = error?.response?.status;

    const isLoginRequest = String(error?.config?.url || '').startsWith('/api/users/login');

    // Login failures belong to the login form. All other 401 responses mean
    // the current session is no longer usable.
    if (status === 401 && !isLoginRequest) {
      const msg = error?.response?.data?.message || 'Unauthorized';
      console.error('Auth error (401):', msg);
      expireSessionOnce();
    } else if (status === 403) {
      // Forbidden: user is authenticated but not allowed (do NOT logout)
      const msg = error?.response?.data?.message || 'Forbidden';
      console.warn('Forbidden (403):', msg);
    }

    return Promise.reject(error);
  }
);

export default secureAxios;
